/**
 * Admin-Shell (P0, docs/KONZEPT-ADMIN-PANEL.md § Frontend "eigene Seiten unter
 * apps/web/src/pages/admin/*"): minimale Bestaetigungsseite fuer den Host-Sonderfall
 * `maestro.<APP_BASE_DOMAIN>`. Das Host-Gate selbst lebt in App.tsx (`istAdminHost`) - diese
 * Komponente prueft den Host nicht nochmal, sie wird nur dann ueberhaupt gerendert.
 *
 * `GET /api/admin/whoami` (apps/api/src/routes/admin.ts) ist server-seitig bewusst KEIN
 * Existenz-Orakel: falscher Host UND eingeloggt-aber-kein-Platform-Admin liefern dasselbe 404.
 * Diese Seite haelt die gleiche Regel im UI-Text ein - der Fehlertext nennt nirgends "Admin"
 * oder "Zugriff verweigert", nur eine neutrale "nicht gefunden"-Meldung, wie sie auch fuer eine
 * frei erfundene URL erscheinen wuerde.
 *
 * P1 (docs/LOOP-ADMIN-PANEL.md § P1 "Frontend Mandantenliste + Detailansicht"): nach
 * erfolgreichem `whoami` zeigt die Shell zusaetzlich die Mandantenliste (`TenantsListe`) bzw.,
 * nach Auswahl einer Zeile, die Detailansicht (`TenantDetail`). Navigation dazwischen laeuft
 * bewusst NICHT ueber den Router (`selectedTenantId`-State statt `<Route>`) - dasselbe Muster
 * wie der `istAdminHost`-Zweig in App.tsx, damit die E2E-Abdeckungs-Ratsche
 * (`apps/web/test/e2e-abdeckung.test.ts`) fuer Admin-Unterseiten nicht anschlaegt.
 *
 * P0 Admin-Bereich-V2 (docs/KONZEPT-ADMIN-BEREICH.md, docs/LOOP-ADMIN-BEREICH.md § P0): Topbar +
 * whoami-Badge + Impersonations-Banner sitzen jetzt in `AdminShell.tsx`, gerendert NUR nach
 * erfolgreichem `whoami` (siehe dortiger Kopfkommentar zum Existenz-Orakel-Grundsatz) - Lade- und
 * 404-Zustand bleiben unveraendert unmarkiert.
 *
 * P1 (docs/LOOP-ADMIN-BEREICH.md § P1 "Seiten /audit und /impersonationen"): `useAdminNavigation()`
 * wird HIER erstmals eingebunden - bewusst nur fuer die Top-Level-Ziele, die damals schon als Seite
 * existierten (`mandanten`/`audit`/`impersonationen`; `mandanten-neu`/`admins`/`unbekannt` fielen
 * auf die Mandantenliste zurueck - `mandanten-neu` bekam seinen eigenen Zweig erst mit P2, s.
 * unten). Die Mandanten-DETAILANSICHT bleibt bewusst beim bisherigen
 * `selectedTenantId`-State statt einem `/mandanten/:id`-Pfad: eine volle Umstellung haette die
 * Klick-Navigation dorthin an echte Browser-History gekoppelt, was smtl. bestehende P2/P3-Tests in
 * `admin-shell.test.tsx` (navigieren per `fireEvent.click` auf eine Zeile, danach direkt wieder auf
 * die Ausgangsseite pruefend) neu ausgerichtet haette - Aufwand, der ueber dieses Kriterium
 * ("/audit" und "/impersonationen" erreichbar machen") hinausgeht. Schlankere Zwischenloesung: der
 * Hook routet NUR zwischen den Top-Level-Zielen, "Mandanten" faellt beim Klick zusaetzlich seinen
 * lokalen `selectedTenantId`-State zurueck (wie zuvor via `onMandantenClick`).
 *
 * P1 (docs/LOOP-ADMIN-BEREICH.md § P1, sechstes Kriterium "Dashboard-Startseite"): `dashboard`
 * (Pfad "/") ist jetzt ein VIERTES echtes Ziel, nicht mehr Rueckfall auf "mandanten" — der
 * Konzept-Seitenbaum listet "/" explizit als Dashboard (docs/KONZEPT-ADMIN-BEREICH.md §
 * "Informationsarchitektur & UI"). Das aendert den STARTZUSTAND des gesamten Admin-Bereichs: wer
 * die Root-URL oeffnet, sieht jetzt zuerst das Dashboard statt der Mandantenliste. Bewusste
 * Entscheidung (nicht "kleinstmoegliche Aenderung"), weil `route.seite === "dashboard"` GENAU dem
 * Pfad "/" entspricht (`useAdminNavigation.ts` `parseAdminPfad`: `teile.length === 0` ->
 * `dashboard`) — es gibt keinen zweiten, eigenen Pfad fuer das Dashboard, den man stattdessen
 * verdrahten koennte, ohne den Auftrag ("Ziel 'dashboard' ... auf die neue Seite routen, statt
 * Rueckfall auf 'mandanten'") zu verfehlen. Betroffene Bestandstests (`admin-shell.test.tsx`,
 * `admin-tenant-detail-tabs.test.tsx`, `admin-tenant-loeschen.test.tsx`) wurden angepasst: sie
 * navigieren jetzt explizit auf "/mandanten", bevor sie mit der Mandantenliste interagieren (siehe
 * dortige Kopfkommentare) — reine Selektor-Anpassung, das jeweils getestete Verhalten ist
 * unveraendert. `admin-audit-impersonationen-navigation.test.tsx` bekam einen aktualisierten
 * Start-Test (Dashboard statt Mandantenliste) und einen neuen Test fuer die jetzt aktive
 * "Dashboard"-Nav-Kachel.
 *
 * P2 (docs/LOOP-ADMIN-BEREICH.md § P2, siebtes Kriterium "Onboarding-Assistent-UI"): `mandanten-neu`
 * bekommt jetzt einen eigenen Render-Zweig (`MandantNeuSeite`, `./mandant-neu.tsx`) statt auf die
 * Mandantenliste zurueckzufallen - der Nav-Highlight-Zweig `aktiveSeite` bleibt unveraendert bei
 * "mandanten" (kein eigener Topbar-Eintrag fuer den Assistenten, s. Konzept-Seitenbaum: `/mandanten
 * /neu` ist eine Unterseite von `/mandanten`, erreichbar ueber den neuen "Neuer Mandant"-Knopf in
 * `tenants-liste.tsx`, nicht ueber die Topbar). Details zur Navigation zwischen den beiden States
 * (`route.seite`, `selectedTenantId`) direkt am neuen Zweig unten.
 */
import { useState } from "react";
import { useUser } from "@stackframe/react";
import { useApi } from "../../hooks/useApi";
import { useAdminNavigation } from "../../hooks/useAdminNavigation";
import { LoadingRows, EmptyState, ToastProvider } from "../../ui/components";
import { AdminShell } from "./AdminShell";
import { TenantsListe } from "./tenants-liste";
import { TenantDetail } from "./tenant-detail";
import { AuditLogSeite } from "./AuditLogSeite";
import { ImpersonationenSeite } from "./ImpersonationenSeite";
import { DashboardSeite } from "./DashboardSeite";
import { MandantNeuSeite } from "./mandant-neu";
import { AdminsSeite } from "./AdminsSeite";

interface AdminWhoami {
  adminId: string;
}

export default function AdminIndexPage() {
  // Erzwingt Login wie das normale ProtectedLayout (App.tsx) - ohne eingeloggten Nutzer kann
  // `whoami` ohnehin nicht antworten (Bearer-Token noetig, siehe lib/api.ts).
  useUser({ or: "redirect" });

  const { data, loading, error } = useApi<AdminWhoami>("/api/admin/whoami");
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const { route, navigiereZu } = useAdminNavigation();

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <LoadingRows rows={3} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <EmptyState icon="search_off" title="Nicht gefunden" hint="Diese Seite existiert nicht." />
      </div>
    );
  }

  // Nur die bereits gebauten Top-Level-NAV-Ziele werden hier unterschieden - alles andere
  // (mandanten/mandanten-neu/mandanten-detail/admins/unbekannt) faellt fuer die NAV-HERVORHEBUNG
  // auf "mandanten" zurueck (s. Kopfkommentar). Was INNERHALB von "mandanten" tatsaechlich
  // gerendert wird (Liste/Detail/Onboarding-Assistent), entscheidet `route.seite` weiter unten -
  // `aktiveSeite` ist NUR fuer `AdminShell`s Nav-Hervorhebung da.
  const aktiveSeite: "dashboard" | "mandanten" | "audit" | "impersonationen" | "admins" =
    route.seite === "audit" || route.seite === "impersonationen" || route.seite === "dashboard" || route.seite === "admins"
      ? route.seite
      : "mandanten";

  function onNavigate(pfad: string) {
    if (pfad === "/mandanten") setSelectedTenantId(null);
    navigiereZu(pfad);
  }

  return (
    // P4 (docs/LOOP-ADMIN-BEREICH.md, fuenftes P4-Kriterium "Betriebsschalter-UI"): `ToastProvider`
    // (P0-Baustein, `ui/components.tsx`) umschliesst hier ERSTMALS die ganze Admin-Shell - bisher
    // reine Infrastruktur ohne Aufrufer (P0-Kopfkommentar "kein bestehender window.alert-Aufruf
    // wird ersetzt"). `EinstellungenTab.tsx` ist der erste Aufrufer von `useToast()`.
    <ToastProvider>
      <AdminShell adminId={data.adminId} aktiveSeite={aktiveSeite} onNavigate={onNavigate}>
        {aktiveSeite === "dashboard" ? (
          <DashboardSeite onNavigate={onNavigate} />
        ) : aktiveSeite === "audit" ? (
          <AuditLogSeite />
        ) : aktiveSeite === "impersonationen" ? (
          <ImpersonationenSeite />
        ) : aktiveSeite === "admins" ? (
          <AdminsSeite />
        ) : route.seite === "mandanten-neu" ? (
          // Admin-Bereich V2 P2 (docs/LOOP-ADMIN-BEREICH.md, siebtes P2-Kriterium): eigener Zweig
          // ueber `route.seite` (nicht `aktiveSeite` - das faltet "mandanten-neu" bewusst mit auf
          // "mandanten" fuer die Nav-Hervorhebung zusammen, s. Kommentar oben). "Später fortsetzen"/
          // "Abbrechen" navigiert per `onNavigate` zurueck auf die Liste; "Fertig" navigiert
          // ZUSAETZLICH `selectedTenantId` direkt auf den frisch angelegten Mandanten (NICHT ueber
          // `onNavigate`, das wuerde `selectedTenantId` fuer den Pfad "/mandanten" wieder auf `null`
          // zuruecksetzen) - beide States committen gebuendelt (React 18 automatic batching), die
          // Detailseite erscheint direkt, kein Zwischenschritt ueber die leere Liste.
          <MandantNeuSeite
            onAbbrechen={() => onNavigate("/mandanten")}
            onFertig={(tenantId) => {
              navigiereZu("/mandanten");
              setSelectedTenantId(tenantId);
            }}
          />
        ) : selectedTenantId ? (
          <TenantDetail tenantId={selectedTenantId} onBack={() => setSelectedTenantId(null)} />
        ) : (
          <TenantsListe onSelectTenant={setSelectedTenantId} onNeu={() => onNavigate("/mandanten/neu")} />
        )}
      </AdminShell>
    </ToastProvider>
  );
}
